import React, { useEffect, useContext } from "react"
import "./Navbar.css"

import NavbarItem from "./NavbarItem"

import MenuIcon from "../Icons/MenuIcon"
import DashboardIcon from "../Icons/Navbar/DashboardIcon"
import FieldsIcon from "../Icons/Navbar/FieldsIcon"
import ToolsIcon from "../Icons/Navbar/ToolsIcon"
import SettingsIcon from "../Icons/Navbar/SettingsIcon"
import AboutIcon from "../Icons/Navbar/AboutIcon"
import UserManualIcon from "../Icons/Navbar/UserManualIcon"

import NavbarOptions from "./NavbarOptions"
import NavbarFields from "../Navbar/NavbarFields"
import useOutsideComponentClickHandler from "../../Util/ClickOutsideHook"

import { getPolygonData, getTableData } from "../../services/table.service"

import { Link, useParams, useLocation, useHistory } from "react-router-dom"
import { AuthContext } from "../../Auth/Auth"
import { DataContext } from "../../Util/Data"
import { useMemo } from "react"
import HomeIcon from "../Icons/Navbar/HomeIcon"
import { UIAlertContext } from "../UIAlert/UIAlert"
const USER_MANUAL_LINK = "https://drive.google.com/file/d/1g5bzgc0BzCb52p6uivKJTTrYfPBZpKgk/view?usp=sharing"

function Navbar(props) {
    const { view, settings, navItem } = props

    const [open, toggle] = React.useState(false)

    const { id: fieldId } = useParams()
    const location = useLocation()
    const { permissions } = useContext(AuthContext)
    const { aboutModal } = useContext(UIAlertContext)
    const history = useHistory()

    const wrapperRef = useOutsideComponentClickHandler(() => toggle(false))

    const {
        state: { fields },
        dispatch,
    } = useContext(DataContext)

    useEffect(() => {
        if (!fields || fields.length === 0) {
            getTableData()
                .then((fields) => {
                    // save fields to upper state
                    dispatch({ type: "GET_FIELDS", payload: { fields } })
                    // uid array
                    const fieldPolygonsToRetrieve = fields.map((field) => field.uuid)
                    // get polygon data from all fields
                    return getPolygonData(fieldPolygonsToRetrieve)
                })
                .then((polygons) => {
                    // save polygons to upper state
                    dispatch({ type: "GET_POLYGONS", payload: { polygons } })
                })
                .catch(console.log)
        }
    }, [])

    useEffect(() => {
        toggle(false)
    }, [location])

    function handleMenuClick(e) {
        e.preventDefault()
        toggle(true)
    }

    const DASHBOARDS = [
        { to: "general", name: "General Model", permission: "general" },
        { to: "weather", name: "Weather Model", permission: "weather" },
        { to: "satellite", name: "Satellite Model", permission: "satellite" },
    ]

    const OPTIMIZATION_TOOLS = [
        { to: "planting_tool", name: "Planting Tool", permission: "planting_tool" },
        { to: "harvesting_tool", name: "Harvesting Tool", permission: "harvesting_tool" },
        { to: "spraying_tool", name: "Spraying Tool", permission: "spraying_tool" },
        { to: "nitrogen_tool", name: "Spraying Tool", permission: "nitrogen_tool" },
        {
            to: "nitrogen_application_tool",
            name: "Nitrogen Application Tool",
            permission: "nitrogen_application_tool",
        },
    ]

    const SETTINGS = [
        // { to: "profile-settings", name: "Profile Settings" },
        { to: "alerts-settings-individual", name: "Individual Alerts" },
        // TODO reenable stacked alerts when they are stable again
        // { to: "alerts-settings-stacked", name: "Stacked Alerts" },
        { to: "crop-settings", name: "Crop & Variety Settings" },
        { to: "table-settings", name: "Table Settings" },
    ]

    let defaultView = useMemo(() => {
        return DASHBOARDS.find((dashboard) => {
            return permissions[dashboard.permission]
        }, [])
    }, [permissions])

    const openAbout = () => {
        aboutModal()
    }

    return (
        <>
            <div className="mobile-header">
                <MenuIcon onClick={handleMenuClick} className="mobile-header__icon" />
            </div>
            <nav className={"navbar" + (open ? " expanded" : "")} onClick={() => toggle(true)} ref={wrapperRef}>
                <Link to="/dashboard">
                    <div className="navbar__logo">
                        <img className={"navbar__header-image" + (open ? "" : " icon")} src="/skip-logo.png" />
                    </div>
                </Link>
                <div className="navbar__content">
                    <div className="navbar__content-top">
                        <NavbarItem 
                            navbarOpen={open} 
                            icon={HomeIcon} 
                            name="Home" 
                            onClick={() => history.push("/")} 
                        />
                        <NavbarItem 
                            navbarOpen={open}
                            icon={FieldsIcon} 
                            name="My Fields" 
                            grow
                        >
                            <NavbarFields
                                linkComponent={Link}
                                currentRegion="Cavendish"
                                view={view || defaultView}
                                items={fields.map((field) => ({
                                    ...field,
                                    to: "/" + (settings ? defaultView.to : view) + "/" + field.uuid,
                                }))}
                            />
                        </NavbarItem>
                        {!settings && (
                            <>
                                <NavbarItem 
                                    navbarOpen={open}
                                    active={navItem === "dashboards"} 
                                    icon={DashboardIcon} 
                                    name="Dashboards"
                                >
                                    <NavbarOptions
                                        linkComponent={Link}
                                        fieldId={fieldId}
                                        view={view}
                                        items={DASHBOARDS.filter(
                                            (item) =>
                                                (item.permission && permissions[item.permission]) || !item.permission
                                        )}
                                    />
                                </NavbarItem>
                                <NavbarItem 
                                    navbarOpen={open}
                                    active={navItem === "tools"} 
                                    icon={ToolsIcon} 
                                    name="Optimization Tools"
                                >
                                    <NavbarOptions
                                        linkComponent={Link}
                                        fieldId={fieldId}
                                        view={view}
                                        items={OPTIMIZATION_TOOLS.filter(
                                            (item) =>
                                                (item.permission && permissions[item.permission]) || !item.permission
                                        )}
                                    />
                                </NavbarItem>
                            </>
                        )}
                        <NavbarItem 
                            navbarOpen={open}
                            active={settings} 
                            icon={SettingsIcon} 
                            name="Settings"
                        >
                            <NavbarOptions view={view} linkComponent={Link} items={SETTINGS} />
                        </NavbarItem>
                    </div>
                    <div className="navbar__content-bottom">
                        <NavbarItem
                        navbarOpen={open}
                            icon={UserManualIcon}
                            onClick={() => window.open(USER_MANUAL_LINK)}
                            name="User Manual"
                        />
                        <NavbarItem 
                            navbarOpen={open}
                            icon={AboutIcon} 
                            onClick={() => openAbout()} 
                            name="About"
                        />
                    </div>
                    
                </div>
            </nav>
        </>
    )
}

export default Navbar
